import Breadcrumb from '~/src/common/components/Breadcrumb';
import Icon from '~/src/common/components/Icon';
import OriginFlag from '~/src/common/components/OriginFlag';
import { Tooltip } from '~/src/common/components/Tooltip';
import { useGetCartProductQuantity } from '~/src/common/hooks/cart/useGetCartProductQuantity';
import dayjs from '~/src/common/services/Date';
import I18n from '~/src/common/services/I18n';
import { useNiceModal } from '~/src/common/services/ModalsManager';
import Tracker from '~/src/common/services/Tracker';
import { ImageParams } from '~/src/common/typings/image';
import { ProductDetailed } from '~/src/common/typings/product';
import { getImageUrlFromFormat } from '~/src/common/utils/cloudinary';
import {
  formatBatchDiscountLabel,
  formatPromoLabel,
  getPriceGranularity,
  getPricePerWeight,
} from '~/src/common/utils/prices';
import {
  getProductPromoWithBulkDiscount,
  SPECIFIC_TAG_ATTRIBUTE_KEY,
} from '~/src/common/utils/product';
import { GetDetailBySlug200BreadcrumbsItem } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import MyProductsFocusButton from '~/src/screens/MyProducts/components/Buttons/MyProductsFocusButton';
import { useMyProductsButton } from '~/src/screens/MyProducts/hooks/useMyProductsButton';

import * as GS from '../../layout';

import AddToCartAction from './AddToCartAction';
import * as S from './layout';
import ReinsuranceBannerLight from './ReinsuranceBannerLight';

interface Props {
  product: ProductDetailed;
  freeQuantity: number;
  categoriesBreadcrumbs: Partial<GetDetailBySlug200BreadcrumbsItem>[];
  onRefresh: () => void;
}

const ProductHeaderPane = ({ product, freeQuantity, onRefresh, categoriesBreadcrumbs }: Props) => {
  const {
    attributes,
    consumptionDate,
    name,
    origin,
    itemDefinition,
    itemPrice,
    weightPrice,
    rating,
    packSize,
  } = product;
  // Infos venant du panier
  const cartQuantity = useGetCartProductQuantity({ sku: product.sku, id: product.id });

  // Tag personnalisé
  const customTagAttr = attributes.find(({ key }) => key === SPECIFIC_TAG_ATTRIBUTE_KEY);
  const customTag = typeof customTagAttr?.value === 'string' ? customTagAttr.value : undefined;
  const hasCustomTag = customTag != null && customTag.trim().length > 0;

  // Tag promotion
  const promo = getProductPromoWithBulkDiscount(product);
  const hasPromoTag = promo != null;
  const isPromoBatchDiscount = promo?.mechanism === 'BATCH_DISCOUNT';

  // Date/période de la promotion
  const hasPromoRange = promo?.active != null && promo.conditions?.endDate != null;
  const promoStartDate = dayjs(promo?.conditions?.startDate).format('DD/MM');
  const promoEndDate = dayjs(promo?.conditions?.endDate).format('DD/MM');
  const isDayPromo = promoStartDate === promoEndDate;
  const promoRange = { startDate: promoStartDate, endDate: promoEndDate };
  const promoLabel = isDayPromo
    ? I18n.t('view-product-screen.promo-day', promoRange)
    : I18n.t('view-product-screen.promo-range', promoRange);

  // Visibilité des tags (custom vs promo)
  const showPromoTag = hasPromoTag;
  const showPromoRange = showPromoTag && hasPromoRange;
  const showCustomTag = hasCustomTag && !hasPromoTag;
  const showTags = showPromoTag || showCustomTag;

  // Notes et avis clients
  const showRating = rating != null;

  const { show } = useNiceModal('product-ratings-modal');
  const openRatingsModal = () => {
    const isHomeProduct = categoriesBreadcrumbs.length < 2;
    const subcategory = categoriesBreadcrumbs[isHomeProduct ? 0 : 1].name;
    const category = isHomeProduct ? 'Accueil' : categoriesBreadcrumbs[0].name;

    void show({ id: product.sku });

    Tracker.sendEvent('view all reviews', {
      'product id': product.id,
      'product name': product.name,
      'product category': category ?? '',
      'product subcategory': subcategory,
      'product rating': rating?.average,
      'nb of ratings': rating?.nbRatings,
      'SKU': product.sku,
    });
  };

  // Poids et granularité
  const pricePerWeight = weightPrice ? getPricePerWeight(weightPrice) : '';
  const granularityLabel = getPriceGranularity(itemDefinition, { displayWeight: true, packSize });
  const additionnalPriceInfo = `${granularityLabel}${pricePerWeight ? ` - ${pricePerWeight}` : ''}`;

  // Labels (AOP, HVE, etc...)
  const labels = product.labels
    .map(item => ({ id: item.id, label: item.label, image: item.images[0] }))
    .filter(item => item.image != null);
  const showLabels = labels.length > 0;

  const myProductTag = useMyProductsButton(product);

  // DLC
  const showDLC = consumptionDate != null;

  return (
    <S.ProductHeaderPaneContainer>
      <GS.MobileBreadcrumbsContainer>
        <Breadcrumb item={product} />
      </GS.MobileBreadcrumbsContainer>
      <GS.StickyContent>
        <GS.StickyContentGroup>
          {showTags ? (
            <GS.StickyContentItem>
              {showPromoTag ? (
                <S.PromoTag $isBatchDiscount={isPromoBatchDiscount}>
                  {isPromoBatchDiscount
                    ? formatBatchDiscountLabel(promo, cartQuantity - freeQuantity)
                    : formatPromoLabel(promo, itemPrice)}
                </S.PromoTag>
              ) : null}
              {showPromoRange ? <S.PromoRange>{promoLabel}</S.PromoRange> : null}
              {showCustomTag ? <GS.LightGreenTag>{customTag}</GS.LightGreenTag> : null}
            </GS.StickyContentItem>
          ) : null}
          <GS.CatalogItemName>{name}</GS.CatalogItemName>
          {showRating ? (
            <GS.StickyContentItem>
              <S.Rating rating={rating} />
              <S.SeeAllRatingsContainer onClick={openRatingsModal}>
                <div>{I18n.t('view-product-screen.show-ratings')} </div>
                <Icon role="img" name={'Chevron-right'} size={14} />
              </S.SeeAllRatingsContainer>
            </GS.StickyContentItem>
          ) : null}
        </GS.StickyContentGroup>
        {myProductTag ? <MyProductsFocusButton {...myProductTag} /> : null}
        {origin != null ? (
          <GS.StickyContentItem>
            <OriginFlag origin={origin} />
            <S.Origin>Origine {origin}</S.Origin>
          </GS.StickyContentItem>
        ) : null}
        <S.Granularity>{additionnalPriceInfo}</S.Granularity>
        {showLabels ? (
          <GS.StickyContentGroup>
            <S.LabelTitle>{I18n.t('view-product-screen.labels-title')}</S.LabelTitle>
            <GS.StickyContentItem>
              {labels.map(({ id, label, image }) => (
                <Tooltip key={id} content={label}>
                  <S.Label
                    src={getImageUrlFromFormat(image, 'square', ImageParams.productLabels)}
                    alt={label}
                  />
                </Tooltip>
              ))}
            </GS.StickyContentItem>
          </GS.StickyContentGroup>
        ) : null}
        {showDLC ? (
          <GS.StickyContentGroup>
            <S.LabelTitle>{I18n.t('view-product-screen.consumption-date')}</S.LabelTitle>
            <GS.StickyContentItem>
              <Icon
                name="calendar"
                size={16}
                aria-label={I18n.t('view-product-screen.consumption-date-aria-label')}
              />
              <S.LabelName>{dayjs(consumptionDate).format('DD/MM/YYYY')}</S.LabelName>
            </GS.StickyContentItem>
          </GS.StickyContentGroup>
        ) : null}
        <AddToCartAction
          product={product}
          freeProductQuantity={freeQuantity}
          onRefresh={onRefresh}
          categoriesBreadcrumbs={categoriesBreadcrumbs}
        />
        <ReinsuranceBannerLight />
      </GS.StickyContent>
    </S.ProductHeaderPaneContainer>
  );
};

export default ProductHeaderPane;
